/** Markets tab (A7Y-4883): storefront and language tables, sortable on every numeric column. */
import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import type { KeywordDatabaseMarkets, KeywordDatabaseStore } from '@a7y/types';
import { SortableHeader } from '@/components/SortableHeader';
import { formatInt } from '../format';

type SortField = 'count' | 'added24h' | 'added7d';
type SortOrder = 'asc' | 'desc';

const STORE_LABEL: Record<KeywordDatabaseStore, string> = {
  appstore: 'App Store',
  playstore: 'Play Store',
};

function useNumericSort() {
  const [sort, setSort] = useState<{ field: SortField; order: SortOrder }>({
    field: 'count',
    order: 'desc',
  });
  const onSort = (field: SortField) =>
    setSort((prev) =>
      prev.field === field
        ? { field, order: prev.order === 'desc' ? 'asc' : 'desc' }
        : { field, order: 'desc' }
    );
  const sorted = <R extends Record<SortField, number>>(rows: readonly R[]): R[] =>
    [...rows].sort((a, b) =>
      sort.order === 'desc' ? b[sort.field] - a[sort.field] : a[sort.field] - b[sort.field]
    );
  return { sort, onSort, sorted };
}

function NumericHeaders({
  sort,
  onSort,
}: {
  sort: { field: SortField; order: SortOrder };
  onSort: (field: SortField) => void;
}) {
  return (
    <>
      <SortableHeader
        field="count"
        label="Keywords"
        sortField={sort.field}
        sortOrder={sort.order}
        onSort={onSort}
      />
      <SortableHeader
        field="added24h"
        label="Added 24 h"
        sortField={sort.field}
        sortOrder={sort.order}
        onSort={onSort}
      />
      <SortableHeader
        field="added7d"
        label="Added 7 d"
        sortField={sort.field}
        sortOrder={sort.order}
        onSort={onSort}
      />
    </>
  );
}

export function MarketsTab({ markets }: { markets: KeywordDatabaseMarkets }) {
  const storefronts = useNumericSort();
  const languages = useNumericSort();

  return (
    <div className="space-y-8">
      <section>
        <h2 className="mb-2 text-sm font-medium text-zinc-900 dark:text-zinc-100">Storefronts</h2>
        <Table dense striped aria-label="Storefronts">
          <TableHead>
            <TableRow>
              <TableHeader>Store</TableHeader>
              <TableHeader>Storefront</TableHeader>
              <NumericHeaders sort={storefronts.sort} onSort={storefronts.onSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {storefronts.sorted(markets.storefronts).map((r) => (
              <TableRow
                key={`${r.store}-${r.country}`}
                data-testid={`storefront-row-${r.store}-${r.country}`}
              >
                <TableCell>{STORE_LABEL[r.store]}</TableCell>
                <TableCell className="font-medium">{r.country}</TableCell>
                <TableCell className="tabular-nums">{formatInt(r.count)}</TableCell>
                <TableCell className="tabular-nums">{formatInt(r.added24h)}</TableCell>
                <TableCell className="tabular-nums">{formatInt(r.added7d)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-medium text-zinc-900 dark:text-zinc-100">Languages</h2>
        <Table dense striped aria-label="Languages">
          <TableHead>
            <TableRow>
              <TableHeader>Language</TableHeader>
              <NumericHeaders sort={languages.sort} onSort={languages.onSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {languages.sorted(markets.languages).map((r) => (
              <TableRow key={r.language} data-testid={`language-row-${r.language}`}>
                <TableCell className="font-medium">{r.language}</TableCell>
                <TableCell className="tabular-nums">{formatInt(r.count)}</TableCell>
                <TableCell className="tabular-nums">{formatInt(r.added24h)}</TableCell>
                <TableCell className="tabular-nums">{formatInt(r.added7d)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>
    </div>
  );
}
